<template>
  <VueFinalModal
    class="createNetMessage__modal"
    content-class="createNetMessage__content"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    :aria-labelledby="'network-modal-title'"
    :aria-describedby="'network-modal-description'"
  >
    <div
      id="network-modal-title"
      class="createNetMessage__title"
    >
      Введите имя сети
    </div>

    <div
      id="network-modal-description"
      class="createNetMessage__subtitle"
    >
      <span class="subtitle">
        Перед созданием точек продаж, необходимо задать "Название Сети". Это
        <span
          class="r-link"
          role="button"
          tabindex="0"
          @click="openFaq"
          @keydown.enter="openFaq"
          @keydown.space="openFaq"
        >
          доменное имя
        </span>
        которое нельзя будет изменить.<br />
      </span>
    </div>

    <p
      class="domain"
      role="text"
      aria-live="polite"
    >
      <!-- https://qbeek.io/ -->
      {{ defaultLink }}/
      <span
        :class="[
          'domain__name',
          {
            'is-invalid':
              (v$?.merchantSlugName?.$error || asyncV$?.merchantSlugName?.$error) &&
              !isAsyncValidating &&
              !isSyncValidating,
            'is-validating': isAsyncValidating || isSyncValidating,
          },
        ]"
        :aria-invalid="
          (v$?.merchantSlugName?.$error || asyncV$?.merchantSlugName?.$error) && !isAsyncValidating && !isSyncValidating
        "
      >
        {{ network.merchantSlugName || 'доменное имя' }}
      </span>
      /subdomain
    </p>
    <div class="createNetMessage__input">
      <ErrorCheck
        :is-error="
          (v$?.merchantSlugName?.$error || asyncV$?.merchantSlugName?.$error) && !isAsyncValidating && !isSyncValidating
        "
        :error-message="v$?.merchantSlugName?.$errors[0]?.$message || asyncV$?.merchantSlugName?.$errors[0]?.$message"
      >
        <network-name-item
          v-model="network.merchantSlugName"
          placeholder="Используйте только латинские прописные буквы"
          :disabled="isLoading"
          :class="{
            'is-invalid':
              (v$?.merchantSlugName?.$error || asyncV$?.merchantSlugName?.$error) &&
              !isAsyncValidating &&
              !isSyncValidating,
            'is-validating': isAsyncValidating || isSyncValidating,
          }"
          :aria-describedby="
            (v$?.merchantSlugName?.$error || asyncV$?.merchantSlugName?.$error) &&
              !isAsyncValidating &&
              !isSyncValidating
              ? 'network-name-error'
              : undefined
          "
          @update:model-value="handleSlugNameChange"
        />
      </ErrorCheck>
    </div>

    <div class="createNetMessage__buttons">
      <action-button
        w100p
        :spin="isLoading"
        :disabled="isLoading"
        @click="closeModal"
      >
        Отмена
      </action-button>
      <action-button
        w100p
        action
        :spin="isLoading"
        :disabled="isLoading || !network.merchantSlugName"
        @click="saveNetworkName"
      >
        Сохранить
      </action-button>
    </div>

    <div class="close-icon">
      <v-icon
        name="close"
        class="modal__close"
        role="button"
        tabindex="0"
        aria-label="Закрыть"
        @click="closeModal"
        @keydown.enter="closeModal"
        @keydown.space="closeModal"
      />
    </div>
  </VueFinalModal>
</template>

<script setup>
import { VueFinalModal } from 'vue-final-modal';
import { ref, reactive, onMounted, onUnmounted, computed } from 'vue';
import slugify from 'slugify';
import NetworkNameItem from '@/components/UI/inputs/TextInput.vue';
import { useVuelidate } from '@vuelidate/core';
import { useToast } from 'vue-toastification';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { updateMerchantSlugName, checkNameUniq } from '@/services/merchant.service';
import i18n from '@/i18n';
import { useAuth } from '@/composables/useAuth';
import { useNetworkValidation } from './composables/useNetworkValidation';
import { debounce } from './utils/debounce';
import { NETWORK_CONSTANTS } from './constants/network';

// Props & Emits
const props = defineProps({
  title: {
    type: String,
    default: 'Title',
  },
  isVisible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  confirm: null,
  close: null,
  open: null,
});

// Default link
const defaultLink = computed(() => {
  return process.env.VUE_APP_WEB_APP_URL;
});

// Composables
const toast = useToast();
const router = useRouter();
const store = useStore();
const { syncValidationRules, asyncValidationRules } = useNetworkValidation();

// Reactive state
const isLoading = ref(false);
const isAsyncValidating = ref(false);
const isSyncValidating = ref(false);
const network = reactive({
  merchantSlugName: '',
});

// Computed
const isFormValid = computed(() => {
  return (
    network.merchantSlugName.length >= NETWORK_CONSTANTS.MIN_NAME_LENGTH &&
    network.merchantSlugName.length <= NETWORK_CONSTANTS.MAX_NAME_LENGTH
  );
});

// Validation - синхронная для кнопки Сохранить
const v$ = useVuelidate(syncValidationRules, network);

// Асинхронная валидация для ввода символов
const asyncV$ = useVuelidate(asyncValidationRules, network);

// Debounced slug name handler
const debouncedSlugName = debounce((value) => {
  const netName = slugify(value, NETWORK_CONSTANTS.SLUGIFY_OPTIONS);
  network.merchantSlugName = netName;
}, NETWORK_CONSTANTS.DEBOUNCE_DELAY);

// Methods
const handleSlugNameChange = (value) => {
  console.log('handleSlugNameChange called with:', value);

  // Update value immediately for validation
  const netName = slugify(value, NETWORK_CONSTANTS.SLUGIFY_OPTIONS);
  network.merchantSlugName = netName;

  // Set validating state to prevent red flashing
  isAsyncValidating.value = true;

  // Trigger async validation immediately (only uniqueness check)
  asyncV$.value.$validate().finally(() => {
    isAsyncValidating.value = false;
  });

  // Also update with debounce for UI
  debouncedSlugName(value);
};

const saveNetworkName = async () => {
  // Устанавливаем состояние синхронной валидации
  isSyncValidating.value = true;

  try {
    // Проверяем синхронную валидацию (required, minLength, maxLength, url)
    const syncResult = await v$.value.$validate();

    if (!syncResult) {
      return;
    }

    // Проверяем асинхронную валидацию (uniqueness)
    const asyncResult = await asyncV$.value.$validate();

    if (!asyncResult) {
      return;
    }

    // Если все валидации прошли, сбрасываем состояния
    isSyncValidating.value = false;
    isAsyncValidating.value = false;

    try {
      isLoading.value = true;
      const result = await updateMerchantSlugName(network.merchantSlugName);

      if (result) {
        store.commit('set_merchantSlugName', network.merchantSlugName);
        toast.success(NETWORK_CONSTANTS.TOAST_MESSAGES.SUCCESS);
        emit('confirm');
      }
    } catch (error) {
      console.error('Error saving network name:', error);
    } finally {
      isLoading.value = false;
    }
  } finally {
    // Сбрасываем состояние валидации в любом случае
    isSyncValidating.value = false;
  }
};

const closeModal = () => {
  v$.value.$reset();
  emit('close');
};

const openFaq = () => {
  emit('close');
  router.push({
    name: 'faq:article',
    params: {
      index: 3,
      subindex: 0,
      locale: i18n.global.locale,
    },
  });
};

// Lifecycle
onMounted(async () => {
  v$.value.$reset();
  network.merchantSlugName = store.getters.getMerchantSlugName;
});

onUnmounted(() => {
  v$.value.$reset();
});
</script>

<style lang="scss">
// @use 'sass:color';

.createNetMessage {
  &__modal {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }

  &__content {
    position: relative;
    max-width: 600px !important;
    max-height: fit-content !important;
    font-weight: 400;
    font-size: 18px;
    line-height: 21px;
    text-align: center;
    color: var(--color-gray-dark);
    padding: 35px 40px;
    border-radius: 6px;
    background: var(--color-white);
    box-shadow: 0 10px 25px var(--color-black-10);
    display: flex !important;
    flex-direction: column !important;
  }

  &__title {
    font-size: 28px;
    line-height: 1.1;
    text-align: center;
    margin-bottom: 10px;
    color: #1f1f1f;
    font-weight: 600;
  }

  &__subtitle {
    font-size: 16px;
    line-height: 1.5;
    text-align: center;
    margin-bottom: 20px;

    .subtitle {
      font-size: 16px;
      line-height: 1.5;
      text-align: center;
      color: #9b9b9b;
    }

    .r-link {
      color: $primary-color;
      cursor: pointer;
      text-decoration: underline;
      transition: color 0.2s ease;

      &:hover,
      &:focus {
        color: var(--color-accent-dark);
        outline: none;
      }

      &:focus-visible {
        outline: 2px solid var(--color-accent);
        outline-offset: 2px;
      }
    }
  }

  &__input {
    font-size: 14px;
    line-height: 21px;
    text-align: center;
    color: #9b9b9b;
    margin-bottom: 24px;
    width: 100%;

    :deep(.network-name-item) {
      &.is-validating {
        border-color: #ffc107;
        box-shadow: 0 0 0 0.2rem rgba(255, 193, 7, 0.25);
      }
    }
  }

  &__buttons {
    @include flex-row(10px);
  }
}

.close-icon {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  color: #9b9b9b;
  font-size: 20px;
  line-height: 24px;
  transition: color 0.2s ease;

  &:hover,
  &:focus {
    color: #1f1f1f;
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }
}

.domain {
  background-color: rgba(var(--color-accent-rgb), 0.1);
  font-size: 1.2rem;
  line-height: 1.5;
  text-align: center;
  padding: 0px 16px;
  margin-bottom: 12px;
  color: #1f1f1f;
  word-break: break-all;
  border-radius: 4px;
  border: 1px solid transparent;

  &__name {
    color: $primary-color;
    font-weight: 600;
    transition: all 0.2s ease;

    &.is-invalid {
      color: #ff0000;
      background-color: rgba(255, 0, 0, 0.1);
      border-radius: 2px;
      padding: 2px 4px;
    }

    &.is-validating {
      color: #ffc107;
      font-weight: 600;
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .createNetMessage {
    &__content {
      width: 90%;
      max-width: 500px;
      padding: 25px 20px;
    }

    &__title {
      font-size: 24px;
    }

    &__subtitle {
      font-size: 14px;
    }
  }

  .domain {
    font-size: 1rem;
    padding: 0px 12px;
  }
}
</style>
